import CommonLayout from "layouts/DashboardLayout";
import React, { useEffect, useRef, useState, useMemo, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Menu,
} from "@mui/material";
import { AgGridTable } from "components/tables/AgGridTable";
import NoDataFound from "common/component/NoDataFound";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import MDButton from "components/MDButton";
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from "../../redux/slices/modalSlice";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useDebounce } from "hooks/useDebounce";
import EditIcon from "common/customIcons/edit";
import DeleteIcon from "common/customIcons/delete";
import { openSnackbar } from "../../redux/slices/common";
import { CloseRounded } from "@mui/icons-material";
import StatusText from "common/component/StatusText";
import {
  deletePromo,
  deletePromotion,
  fetchAllPromotion,
  getAllPromotions,
} from "../../redux/slices/promotions";
import SearchIcon from "common/customIcons/search";
import { getData } from "utilities/common";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import PromoFilter from "./PromoFilter";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats a date string for display without timezone conversion
 * If backend sends UTC Date string, we parse it and display as local date representation
 * @param {string|Date} dateValue - Date string from backend (UTC) or Date object
 * @returns {string} Formatted date string for display
 */
const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return "-";

  try {
    // If it's already a formatted string "YYYY-MM-DD HH:mm:ss", use it directly
    if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateValue)) {
      // Parse as local date and format for display
      const date = dayjs(dateValue, "YYYY-MM-DD HH:mm:ss");
      return date.format("DD MMM, YYYY hh:mm A");
    }

    // If it's a UTC Date string (ISO format), parse it and treat as local
    const date = dayjs(dateValue);
    if (!date.isValid()) return "-";

    // Format for display - this will show the date as entered, not converted
    return date.format("DD MMM, YYYY hh:mm A");
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
};

export default function Promotions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageRef = useRef(0);
  const searchTermRef = useRef("");
  const mainGridApiRef = useRef("");
  const gridRefApi = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { promotions = {}, isLoading: loading = false } = useSelector((state) => state.promotion);
  // const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const fullRoleTab = useMemo(() => searchParams.get("platform"), [searchParams]);

  useLayoutEffect(() => {
    if (!fullRoleTab) {
      setSearchParams({ platform: "hyperwolf" });
    }
  }, [fullRoleTab, setSearchParams]);

  useEffect(() => {
    if (fullRoleTab) {
      pageRef.current = 0;
      const dataSource = {
        getRows,
      };
      if (mainGridApiRef?.current) {
        mainGridApiRef.current.ensureIndexVisible(0, null);
        mainGridApiRef.current.setGridOption("datasource", dataSource);
      }
    }
  }, [fullRoleTab]);

  const getInitialFilters = () => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const filters = {};
      const status = searchParams.get("status");
      if (status && status !== "all") {
        filters.status = status === "true" ? true : status === "false" ? false : status;
      }
      const promotionIdStr = searchParams.get("promotionId");
      if (promotionIdStr) {
        try {
          const pIds = JSON.parse(promotionIdStr);
          if (Array.isArray(pIds) && pIds.length > 0) filters.promotionId = pIds;
        } catch (e) {
          // ignore parsing error
        }
      }
      return filters;
    } catch (error) {
      return {};
    }
  };

  const [addPromotionMenuAnchorEl, setAddPromotionMenuAnchorEl] = useState(null);
  const promotionFiltersPayload = useRef(getInitialFilters());
  const [isLoading, setIsLoading] = useState(true);

  const handleOpenAddPromotionMenu = (event) => {
    setAddPromotionMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAddPromotionMenu = () => {
    setAddPromotionMenuAnchorEl(null);
  };

  // const handleNavigateToAddPromotion = (path) => {
  //   handleCloseAddPromotionMenu();
  //   navigate(path);
  // };

  const handleNavigateToAddPromotion = (path, platform) => {
    handleCloseAddPromotionMenu();
    navigate(path, {
      state: { platform: platform || fullRoleTab?.toLowerCase() },
    });
  };

  const deleteHandler = (promotionId) => {
    dispatch(setModalLoader(true));
    dispatch(deletePromo(promotionId))
      .unwrap()
      .then((res) => {
        showResponseMessage(res?.message, "success");
        dispatch(closeCustomModalDialog());
        pageRef.current = 0;
        const dataSource = {
          getRows,
        };
        mainGridApiRef?.current?.ensureIndexVisible(0, null);
        mainGridApiRef.current.setGridOption("datasource", dataSource);
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        showResponseMessage(err?.message, "error");
      });
  };

  const showResponseMessage = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const columnDefs = [
    {
      headerName: "Promotion Name ",
      field: "name",
      suppressMenu: false,
      minWidth: 180,
      flex: 1,
      //  sortable:false,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
      cellRenderer: (props) => {
        return props?.value?.length > 0 ? (
          <Tooltip title={props?.value} arrow placement="top">
            <Box mr={2} className="text-truncate">
              {props?.value}
            </Box>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      headerName: "Promo Code",
      field: "code",
      suppressMenu: false,
      sortable: false,
      minWidth: 120,
      flex: 1.5,
      cellRenderer: (props) => {
        return props?.value?.length > 0 ? (
          <Tooltip title={props?.value?.join(", ")} arrow placement="top">
            <Box mr={2} className="text-truncate">
              {props?.value?.join(", ")}
            </Box>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      headerName: "Description",
      field: "description",
      suppressMenu: false,
       sortable:false,
      minWidth: 180,
      flex: 1,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
      cellRenderer: (props) => {
        return props?.value?.length > 0 ? (
          <Tooltip title={props?.value} arrow placement="top">
            <Box mr={2} className="text-truncate">
              {props?.value}
            </Box>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    // {
    //   headerName: "Platform",
    //   field: "platform",
    //   suppressMenu: false,
    //   //  sortable:false,
    //   minWidth: 180,
    //   flex: 1,
    //   cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
    //   cellRenderer: (props) => {
    //     return props?.value?.length > 0 ? (
    //       <Tooltip title={props?.value} arrow placement="top">
    //         <Box mr={2} className="text-truncate">
    //           {props?.value}
    //         </Box>
    //       </Tooltip>
    //     ) : (
    //       "-"
    //     );
    //   },
    // },
    // {
    //   headerName: "Promotion Rule",
    //   field: "promoRule",
    //   suppressMenu: false,
    //   minWidth: 180,
    //    sortable:false,
    //   flex: 1,
    //   cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
    //   cellRenderer: (props) => {
    //     return props?.value?.length > 0 ? (
    //       <Tooltip title={props?.value} arrow placement="top">
    //         <Box mr={2} className="text-truncate">
    //           {props?.value}
    //         </Box>
    //       </Tooltip>
    //     ) : (
    //       "-"
    //     );
    //   },
    // },
    {
      headerName: "Limit Per Use",
      field: "usageLimitPerUser",
      suppressMenu: false,
      // sortable: false,
      minWidth: 180,
      flex: 1,
      cellRenderer: (props) => {
        return props.value !== null && props.value !== undefined ? (
          <Box mr={2}>{props.value}</Box>
        ) : (
          "-"
        );
      },
    },
    {
      headerName: "Publish Date",
      field: "publishDate",
      suppressMenu: false,
      //  sortable:false,
      minWidth: 180,
      flex: 1,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
      cellRenderer: (params) => formatDateForDisplay(params.value),
    },
    {
      headerName: "Expiry Date",
      field: "expiryDate",
      //  sortable:false,
      suppressMenu: false,
      minWidth: 180,
      flex: 1,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
      cellRenderer: (params) => formatDateForDisplay(params.value),
    },

    {
      headerName: "Status",
      field: "status",
      sortable: false,
      suppressMenu: false,
      cellRenderer: (props) => {
        return <StatusText status={props?.data?.status} />;
      },
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: "Actions",
      sortable: false,
      cellRenderer: (props) => {
        return (
          <Box display="inline-flex" alignItems="center">
            <IconButton
              disabled={!canEdit}
              onClick={(e) => {
                e.stopPropagation();
                onEditClicked(props);
              }}
              color="primary"
              size="small"
              variant="primary"
            >
              <EditIcon width="16px" height="16px" />
            </IconButton>
            <IconButton
              disabled={!canDelete}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(
                  openCustomModalDialog({
                    modalData: {
                      deleteHandler,
                      deleteId: props.data?._id,
                      deleteType: "promotion",
                    },
                    modal: "DELETE_MODAL",
                  })
                );
              }}
              color="error"
              size="small"
              variant="primary"
              sx={{ marginLeft: 1 }}
            >
              <DeleteIcon width="16px" height="16px" />
            </IconButton>
          </Box>
        );
      },
      cellStyle: { textAlign: "right", justifyContent: "end" },
      headerClass: "right-aligned-cell-header",
      minWidth: 120,
      maxWidth: 100,
      flex: 1,
      pinned: "right",
    },
  ];

  function getSortInfo(data) {
    const sortInfo = {
      sortField: "",
      sortOrder: "",
    };
    data.forEach((item) => {
      sortInfo.sortField = item.colId;
      // Custom sort payload for status: first click -> active, second -> inactive.
      if (item.colId === "status") {
        sortInfo.sortOrder = item.sort === "asc" ? "active" : "inactive";
      } else {
        sortInfo.sortOrder = item.sort === "asc" ? "asc" : "desc";
      }
    });
    return sortInfo;
  }

  const getRows = async (params) => {
    if (!fullRoleTab) {
      params.successCallback([], 0);
      return;
    }
    gridRefApi.current = params;
    let filter = {};
    if (params?.sortModel.length) {
      filter = { ...getSortInfo(params?.sortModel) };
    }
    // params.successCallback(memberships, 15);
    dispatch(
      // getAllPromotions()
      getAllPromotions({
        skip: pageRef.current,
        limit: 20,
        searchTerm: searchTermRef.current,
        ...promotionFiltersPayload.current,
        platform: fullRoleTab?.toLowerCase(),
        ...filter,
      })
    )
      .unwrap()
      .then((res) => {
        const { data = [], total } = res;
        const promotionsArray = data?.map((item) => {
          // Ensure dates are preserved as strings if backend sends them as strings
          // If backend sends UTC Date objects, convert them to formatted strings
          const processedItem = {
            ...item,
            status: item?.status === "active" ? "Active" : "Inactive",
          };

          // Handle publishDate - if it's a Date object or ISO string, convert to formatted string
          if (processedItem.publishDate) {
            if (
              processedItem.publishDate instanceof Date ||
              typeof processedItem.publishDate === "string"
            ) {
              // If it's already a formatted string, keep it; otherwise format it
              if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(processedItem.publishDate)) {
                // It's a Date or ISO string, convert to formatted string
                const date = dayjs(processedItem.publishDate);
                if (date.isValid()) {
                  processedItem.publishDate = date.format("YYYY-MM-DD HH:mm:ss");
                }
              }
            }
          }

          // Handle expiryDate - same logic
          if (processedItem.expiryDate) {
            if (
              processedItem.expiryDate instanceof Date ||
              typeof processedItem.expiryDate === "string"
            ) {
              if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(processedItem.expiryDate)) {
                const date = dayjs(processedItem.expiryDate);
                if (date.isValid()) {
                  processedItem.expiryDate = date.format("YYYY-MM-DD HH:mm:ss");
                }
              }
            }
          }

          return processedItem;
        });
        params.successCallback([...promotionsArray], total);
        pageRef.current = pageRef.current + 20;
      })
      .catch(() => {
        params.successCallback([], 0);
      });
  };

  const searchFn = () => {
    const dataSource = {
      getRows,
    };
    mainGridApiRef?.current?.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption("datasource", dataSource);
  };

  const onRowClicked = (row) => {
    if (row?.colDef?.headerName !== "Actions") {
      navigate(`/promotions/details/${row?.data._id}`, {
        state: { source: "common-promotions" },
      });
    }
  };

  const onEditClicked = (row) => {
    navigate(`/promotions/adds/${row?.data?._id}`);
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (promotions?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [promotions, loading, mainGridApiRef]);

  const handleSearch = useDebounce(() => {
    // Perform search operation with the debounced term
    pageRef.current = 0;
    searchFn();
  }, 500);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);

    // Debounce the search callback
  };

  const applyPromoFilter = (filterData) => {
    pageRef.current = 0;
    promotionFiltersPayload.current = filterData?.queryPayload || {};
    const dataSource = {
      getRows,
    };
    mainGridApiRef.current.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption("datasource", dataSource);
  };

  const getQueryValues = (filterData) => {
    pageRef.current = 0;
    const bodyPayload = filterData?.queryPayload || {};
    promotionFiltersPayload.current = bodyPayload;
    const dataSource = {
      getRows,
    };
    mainGridApiRef.current.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption("datasource", dataSource);
    setIsLoading(false);
  };

  let localUser = getData("login-user-info");
  if (localUser) {
    localUser = JSON.parse(localUser);
  }

  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Promotions", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Promotions", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Promotions", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Promotions", "Delete");
  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Box mb={2} className="cannabinoids-tabs">
          <Stack direction="row" gap={1} sx={{ background: "#ffffff" }}>
            <Button
              variant={fullRoleTab === "Hyperwolf" ? "contained" : "outlined"}
              onClick={() => {
                setSearchParams({ platform: `hyperwolf` });
              }}
              sx={{
                color: "#2E2E3A",
                minWidth: "160px",
                backgroundColor: fullRoleTab === "hyperwolf" ? "#F2F3F7" : "#ffffff",
                "&:hover": {
                  backgroundColor: fullRoleTab === "stilo" ? "#F2F3F7" : "#F2F3F7",
                },
                "&:focus": {
                  backgroundColor: fullRoleTab === "stilo" ? "#F2F3F7" : "#F2F3F7",
                },
              }}
              style={{ boxShadow: "none !important" }}
            >
              Hyperwolf
            </Button>
            <Button
              variant={fullRoleTab === "Hemp" ? "contained" : "outlined"}
              onClick={() => {
                setSearchParams({ platform: `hemp` });
              }}
              sx={{
                color: "#2E2E3A",
                minWidth: "160px",
                backgroundColor: fullRoleTab === "hemp" ? "#F2F3F7" : "#ffffff",
                "&:hover": {
                  backgroundColor: fullRoleTab === "stilo" ? "#F2F3F7" : "#F2F3F7",
                },
                "&:focus": {
                  backgroundColor: fullRoleTab === "stilo" ? "#F2F3F7" : "#F2F3F7",
                },
              }}
              style={{ boxShadow: "none !important" }}
            >
              Hemp
            </Button>
            <Button
              variant={fullRoleTab === "Stilo" ? "contained" : "outlined"}
              onClick={() => {
                setSearchParams({ platform: `stilo` });
              }}
              sx={{
                color: "#2E2E3A",
                minWidth: "160px",
                backgroundColor: fullRoleTab === "stilo" ? "#F2F3F7" : "#ffffff",
                "&:hover": {
                  backgroundColor: fullRoleTab === "stilo" ? "#F2F3F7" : "#F2F3F7",
                },
                "&:focus": {
                  backgroundColor: fullRoleTab === "stilo" ? "#F2F3F7" : "#F2F3F7",
                },
              }}
              style={{ boxShadow: "none !important" }}
            >
              Stilo
            </Button>
          </Stack>
        </Box>

        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant="h4">Promotions</Typography>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Stack
              direction="row"
              justifyContent={{ xs: "start", sm: "end" }}
              flexWrap="wrap"
              alignItems="stretch"
              gap={2}
            >
              <TextField
                className="custom-search-field"
                placeholder="Search"
                label=""
                value={searchTerm}
                onChange={(e) => searchHandler(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton size="small">
                        <SearchIcon width="18px" height="18px" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment:
                    searchTerm?.length > 0 ? (
                      <InputAdornment>
                        <IconButton onClick={() => searchHandler("")} size="small">
                          <CloseRounded />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
                style={{ minWidth: "300px" }}
              />
              {/* {canCreate && ( */}
              <MDButton
                startIcon={<AddRoundedIcon fontSize="large" />}
                variant="contained"
                color="primary"
                style={{ minWidth: "200px" }}
                aria-controls={Boolean(addPromotionMenuAnchorEl) ? "add-promotion-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(addPromotionMenuAnchorEl) ? "true" : undefined}
                // onClick={handleOpenAddPromotionMenu}
                onClick={() => handleNavigateToAddPromotion("/promotions/adds")}
              >
                Add Promotion
              </MDButton>
              {/* )} */}
              {/* <Menu
                id="add-promotion-menu"
                anchorEl={addPromotionMenuAnchorEl}
                open={Boolean(addPromotionMenuAnchorEl)}
                onClose={handleCloseAddPromotionMenu}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={() => handleNavigateToAddPromotion("/promotions/adds", "hyperwolf")}
                >
                  Hyperwolf
                </MenuItem>
                <MenuItem onClick={() => handleNavigateToAddPromotion("/promotions/adds", "hemp")}>
                  Hemp
                </MenuItem>
                <MenuItem onClick={() => handleNavigateToAddPromotion("/promotions/adds", "stilo")}>
                  Stilo
                </MenuItem>
              </Menu> */}
            </Stack>
          </Grid>
        </Grid>

        <PromoFilter
          platform={fullRoleTab}
          applyPromoFilter={applyPromoFilter}
          getQueryValues={getQueryValues}
          setIsLoading={setIsLoading}
          promotionFiltersPayload={promotionFiltersPayload?.current}
        />

        {canView && fullRoleTab && (
          <AgGridTable
            col={columnDefs}
            getRows={getRows}
            onRowClicked={onRowClicked}
            mainGridApiRef={mainGridApiRef}
            pageRef={pageRef}
            isLoading={loading}
            gridOptions={{
              noRowsOverlayComponent: NoDataFound,
            }}
            style={{ height: "70vh" }}
          />
        )}
      </Box>
    </CommonLayout>
  );
}
